import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Loader2, Box, Download, Eye, RefreshCw, AlertCircle, Wand2, QrCode, Share2, X, Smartphone, ImageIcon, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useClient } from '../../contexts/ClientContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { useTaskCost } from '../../hooks/useTaskCost';
import { getApiUrl } from '../../utils/config';
import { ImageTo3DForm, ImageTo3DData } from '../ai/ImageTo3DForm';
import AuthenticatedImage from '../media/AuthenticatedImage';
import mediaService from '../../services/mediaService';
import { generateCompleteARUrl, getARUrlDebugInfo } from '../../utils/arUrlGenerator';
import QRCodeGenerator from '../ar/QRCode';
import '@google/model-viewer';
import { div } from 'framer-motion/client';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string;
        alt?: string;
        ar?: boolean;
        'ar-modes'?: string;
        'camera-controls'?: boolean;
        'shadow-intensity'?: string;
        'auto-rotate'?: boolean;
        style?: React.CSSProperties;
        loading?: string;
        'interaction-prompt'?: string;
      };
    }
  }
}

interface TaskStatus {
  task_id: string;
  status: string;
  progress_percentage: number;
  is_completed: boolean;
  is_failed: boolean;
  error_message?: string;
  output_media_file_ids: string[];
  model_url?: string;
}

export const ARFlowManager: React.FC = () => {
  const { currentClient } = useClient();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Task cost and tier limits for 3D generation
  const imageTo3DTask = useTaskCost('Image to 3D Model Generation');

  // Form state
  const [formData, setFormData] = useState<ImageTo3DData>({
    input_media_file_id: '',
    enable_pbr: true,
    target_polycount: 15000,
    texture_richness: 'medium',
  });

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // 3D Model state
  const [modelUrl, setModelUrl] = useState<string | null>(null);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [generatedMediaId, setGeneratedMediaId] = useState<string | null>(null);

  // AR Ad generation state
  const [isGeneratingArAd, setIsGeneratingArAd] = useState(false);
  const [generatedArAdUrl, setGeneratedArAdUrl] = useState<string | null>(null);
  const [arViewerUrl, setArViewerUrl] = useState<string | null>(null);
  const [showArQrCode, setShowArQrCode] = useState(false);
  const [arAdPollingInterval, setArAdPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Removed existing 3D model selection - moved to AR Studio
  const [showMediaSelector, setShowMediaSelector] = useState(false);

  // Removed source image selection for existing models - moved to AR Studio
  const [isLoadingSourceImages, setIsLoadingSourceImages] = useState(false);

  const { toast: showToast } = useToast();

  // Fetch source images from media library
  const fetchSourceImages = useCallback(async () => {
    if (!currentClient) return;

    try {
      setIsLoadingSourceImages(true);
      const response = await fetch(`${getApiUrl()}/api/v1/media?media_type=image&per_page=50`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setExistingModelSourceImages(data.files || []);
      }
    } catch (error) {
      console.error('🎯 Error fetching source images:', error);
    } finally {
      setIsLoadingSourceImages(false);
    }
  }, [currentClient]);

  // Fetch existing 3D models from media library
  const fetchExisting3DModels = useCallback(async () => {
    if (!currentClient) return;

    setIsLoadingExisting3DModels(true);
    try {
      console.log('📦 Fetching existing 3D models...');
      const apiUrl = getApiUrl();
      const fullUrl = `/api/v1/media?media_type=other&page=1&per_page=50`;
      console.log('📦 API URL:', apiUrl);
      console.log('📦 Using relative URL:', fullUrl);
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });

      console.log('📦 Media API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📦 Media API response data:', data);

        // Filter for 3D model files (GLB, GLTF, etc.)
        const allFiles = data.files || data.media_files || [];
        const models3D = allFiles.filter((file: any) =>
          file.mime_type === 'model/gltf-binary' ||
          file.mime_type === 'model/gltf+json' ||
          file.original_filename?.toLowerCase().endsWith('.glb') ||
          file.original_filename?.toLowerCase().endsWith('.gltf')
        );

        console.log('📦 All media files:', allFiles.length);
        console.log('📦 Filtered 3D models:', models3D.length);
        console.log('📦 3D models:', models3D);

        setExisting3DModels(models3D);
      } else {
        const errorData = await response.text();
        console.error('📦 Media API error:', response.status, errorData);
        throw new Error(`API returned ${response.status}: ${errorData}`);
      }
    } catch (error) {
      console.error('📦 Error fetching 3D models:', error);
      showToast({
        title: "Error",
        description: "Failed to load existing 3D models",
        variant: "destructive",
      });
    } finally {
      setIsLoadingExisting3DModels(false);
    }
  }, [currentClient, showToast]);

  // Removed auto-loading of existing 3D models - functionality moved to AR Studio

  // Debug AR ad state changes
  useEffect(() => {
    console.log('🎨 AR Ad State Debug:', {
      isGeneratingArAd,
      generatedArAdUrl,
      arViewerUrl,
      showArQrCode
    });
  }, [isGeneratingArAd, generatedArAdUrl, arViewerUrl, showArQrCode]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (arAdPollingInterval) {
        clearInterval(arAdPollingInterval);
      }
    };
  }, [pollingInterval, arAdPollingInterval]);

  // Removed automatic task resumption to prevent duplicate generations

  const startPolling = useCallback((taskId: string) => {
    // Clear any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'X-Client-ID': currentClient?.id || '',
          },
        });

        if (response.ok) {
          const task = await response.json();
          console.log('🔍 Task status received:', task);

          if (task.status && task.status.toUpperCase() === 'COMPLETED') {
            // Task completed successfully
            clearInterval(interval);
            setPollingInterval(null);
            setIsGenerating(false);

            if (task.output_media_file_ids && task.output_media_file_ids.length > 0 && currentClient) {
              console.log('🔍 Getting 3D model file:', task.output_media_file_ids[0]);

              const mediaFile = await mediaService.getMediaFile(task.output_media_file_ids[0], currentClient.id);
              console.log('🔍 Media file response:', mediaFile);

              if (mediaFile && mediaFile.file_url) {
                console.log('✅ Setting model URL:', mediaFile.file_url);
                setModelUrl(mediaFile.file_url);
                setGeneratedMediaId(mediaFile.id);
                setIsModelLoading(true);

                setTimeout(() => {
                  setIsModelLoading(false);
                }, 5000);
              } else {
                console.error('❌ No file_url in media file:', mediaFile);
              }
            } else {
              console.error('❌ No output_media_file_ids in task:', task);
            }

            // Refresh user data to update credits
            try {
              await refreshUser();
            } catch (refreshError) {
              console.warn('Failed to refresh user data:', refreshError);
            }

            showToast({
              title: "🎉 3D Model Ready!",
              description: "Your 3D model has been generated successfully!"
            });
          } else if (task.status && (task.status.toLowerCase() === 'failed' || task.status.toUpperCase() === 'FAILED')) {
            // Task failed
            clearInterval(interval);
            setPollingInterval(null);
            setIsGenerating(false);

            // Refresh user data on failure to ensure credits are properly handled
            try {
              await refreshUser();
            } catch (refreshError) {
              console.warn('Failed to refresh user data after task failure:', refreshError);
            }

            showToast({
              title: "Generation Failed",
              description: "3D model generation failed. No credits were deducted. Please try again.",
              variant: "destructive"
            });
          }
        }
      } catch (error) {
        console.error('Error polling task status:', error);
      }
    }, 3000); // Poll every 3 seconds

    setPollingInterval(interval);
  }, [currentClient, pollingInterval, showToast]);

  // Model-viewer event handlers
  const handleModelLoad = useCallback(() => {
    console.log('🎉 Model loaded successfully');
    setIsModelLoading(false);
  }, []);

  const handleModelError = useCallback((event: any) => {
    console.error('❌ Model load error:', event);
    setIsModelLoading(false);
    showToast({
      title: "Model Load Error",
      description: "Failed to load the 3D model",
      variant: "destructive",
    });
  }, [showToast]);

  const handleStartGeneration = async (data: ImageTo3DData) => {
    // Prevent duplicate calls
    if (isGenerating) {
      console.log('🚫 Generation already in progress, ignoring duplicate call');
      return;
    }

    if (!currentClient) {
      showToast({
        title: "Error",
        description: "No client selected",
        variant: "destructive",
      });
      return;
    }

    // Check tier limits before processing
    if (imageTo3DTask.isLimitReached) {
      showToast({
        title: "Limit Reached",
        description: `You've reached your monthly 3D model generation limit (${imageTo3DTask.usage}/${imageTo3DTask.limit}). Upgrade your plan for more generations.`,
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('🚀 Starting 3D generation (single call)');
      setIsGenerating(true);
      setTaskStatus(null);
      setModelUrl(null);

      // Get the selected image URL for preview
      if (data.input_media_file_id) {
        try {
          const mediaResponse = await fetch(`${getApiUrl()}/api/v1/media/${data.input_media_file_id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'X-Client-ID': currentClient.id,
            },
          });

          if (mediaResponse.ok) {
            const mediaFile = await mediaResponse.json();
            setSelectedImageUrl(mediaFile.thumbnail_url || mediaFile.file_url);
          }
        } catch (error) {
          console.error('Error fetching media file:', error);
        }
      }

      console.log('🚀 Sending 3D generation request:', data);

      const response = await fetch(`${getApiUrl()}/api/v1/ai/image-to-3d`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start 3D generation');
      }

      const result = await response.json();

      setCurrentTaskId(result.task?.id || result.task_id);

      // Start polling for status
      startPolling(result.task?.id || result.task_id);

      showToast({
        title: "Generation Started",
        description: "Your 3D model is being generated. This may take several minutes.",
      });

    } catch (error: any) {
      setIsGenerating(false);

      if (error.message.includes('Insufficient credits')) {
        showToast({
          title: "Insufficient Credits",
          description: "You don't have enough credits for 3D generation. Please purchase more credits.",
          variant: "destructive",
        });
      } else {
        showToast({
          title: "Error",
          description: error.message || "Failed to start 3D generation",
          variant: "destructive",
        });
      }
    }
  };

  const handleDownload3DModel = async () => {
    if (!modelUrl) return;

    try {
      const response = await fetch(modelUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `3d-model-${Date.now()}.glb`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({
        title: "Download Started",
        description: "Your 3D model is being downloaded.",
      });
    } catch (error) {
      showToast({
        title: "Download Failed",
        description: "Failed to download the 3D model.",
        variant: "destructive",
      });
    }
  };

  const handleCreateARAdFromModel = () => {
    if (!generatedMediaId || !formData.input_media_file_id) {
      showToast({
        title: "Error",
        description: "Missing model or source image data",
        variant: "destructive",
      });
      return;
    }

    // Navigate to AR page with model and source image pre-selected
    navigate('/app/content-hub/ar-studio', {
      state: {
        preSelectedModel: generatedMediaId,
        preSelectedSourceImage: formData.input_media_file_id,
        sourceImageUrl: selectedImageUrl
      }
    });

    showToast({
      title: "Navigating to AR Studio",
      description: "Your 3D model and source image are pre-selected for AR ad generation.",
    });
  };

  const handleReset = () => {
    console.log('🔄 Performing reset...');

    // Clear polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    // Reset all state
    setIsGenerating(false);
    setCurrentTaskId(null);
    setTaskStatus(null);
    setModelUrl(null);
    setIsModelLoading(false);
    setSelectedImageUrl(null);
    setGeneratedMediaId(null);
    setFormData({
      input_media_file_id: '',
      enable_pbr: true,
      target_polycount: 30000,
      texture_richness: 'medium',
    });

    showToast({
      title: "Reset Complete",
      description: "3D Model Studio has been reset to start fresh.",
    });
  };

  const handleGenerateARAdFromExisting = async () => {
    if (!selectedExisting3DModel || !currentClient) {
      showToast({
        title: "Error",
        description: "Please select a 3D model first",
        variant: "destructive",
      });
      return;
    }

    if (!selectedSourceImage) {
      showToast({
        title: "Source Image Needed",
        description: "Please select a source image to create your AR content",
        variant: "default",
      });
      return;
    }

    // Find the selected 3D model details
    const selectedModel = existing3DModels.find(model => model.id === selectedExisting3DModel);
    if (!selectedModel) {
      showToast({
        title: "Error",
        description: "Selected 3D model not found",
        variant: "destructive",
      });
      return;
    }

    // Use the 3D model as both the input and the generated model
    setGeneratedMediaId(selectedExisting3DModel);
    setModelUrl(`/api/v1/media/${selectedExisting3DModel}/3d-model`);

    // Find the selected source image details
    const selectedSourceImageDetails = existingModelSourceImages.find(img => img.id === selectedSourceImage);
    if (!selectedSourceImageDetails) {
      showToast({
        title: "Error",
        description: "Selected source image not found",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('🎯 Using selected source image:', selectedSourceImage);
      console.log('🎯 Using 3D model:', selectedExisting3DModel);

      // Generate AR ad using the existing model and selected source image
      await generateARAdWithExistingModel(selectedExisting3DModel, selectedSourceImage, selectedSourceImageDetails.thumbnail_url || selectedSourceImageDetails.file_url || '');
    } catch (error) {
      console.error('🎯 Error finding input image:', error);
      showToast({
        title: "Error",
        description: "Failed to find an input image for AR ad generation",
        variant: "destructive",
      });
    }
  };

  const generateARAdWithExistingModel = async (modelId: string, inputImageId: string, thumbnailUrl: string) => {
    setIsGeneratingArAd(true);
    setGeneratedArAdUrl(null);

    try {
      // Generate AR viewer URL using centralized utility (matching old AR Studio)
      const useMockForAR = import.meta.env.VITE_USE_MOCK_3D === 'true';
      const viewerUrl = generateCompleteARUrl(
        modelId || 'mock',
        thumbnailUrl,
        useMockForAR
      );

      // Debug information (matching old AR Studio)
      const debugInfo = getARUrlDebugInfo();
      console.log('🎯 AR Debug Info:', debugInfo);
      console.log('🎯 Generated AR Viewer URL:', viewerUrl);
      setArViewerUrl(viewerUrl);

      console.log('🎯 Generated AR Viewer URL:', viewerUrl);
      console.log('🎯 Using input image ID:', inputImageId);
      console.log('🎯 Using 3D model ID:', modelId);

      // Create a prompt that focuses on AR demonstration rather than the specific input image
      const selectedModelName = existing3DModels.find(m => m.id === modelId)?.original_filename || '3D model';
      const enhancedPrompt = `Create a realistic AR visual showing a hand holding a smartphone displaying a ${selectedModelName} in augmented reality. The phone screen shows the 3D model overlaid on a real environment with proper lighting and scale. Professional AR demonstration with realistic smartphone interaction. Focus on the AR technology and smartphone interface, not the specific background product.`;

      // Generate the AR ad creative using the separate input image
      const endpoint = `${getApiUrl()}/api/v1/ai/enhance-product`;
      const payload = {
        input_media_file_id: inputImageId, // Use the found image as input
        description: enhancedPrompt,
        ad_intent: 'product-showcase',
        format_composition: 'with-person-model',
        style_art_direction: 'tech-futuristic',
        skip_prompt_generation: true,
        ar_viewer_url: viewerUrl
      };

      console.log('🎯 AR Ad payload:', payload);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🎯 AR Ad generation failed:', errorText);
        throw new Error(errorText || 'Failed to create AR ad task');
      }

      const result = await response.json();
      console.log('🎯 AR Ad task created:', result);

      if (result.task && result.task.id) {
        startArAdPolling(result.task.id);
      }

      showToast({
        title: "AR Ad Generation Started",
        description: "Your AR content is being generated...",
      });

    } catch (error: any) {
      console.error('🎯 Error creating AR ad:', error);
      showToast({
        title: "Error",
        description: `Failed to start AR ad generation: ${error.message}`,
        variant: "destructive",
      });
      setIsGeneratingArAd(false);
    }
  };

  const generateARAdWithOriginalImage = async (inputImageId: string, modelId: string, thumbnailUrl: string) => {
    setIsGeneratingArAd(true);
    setGeneratedArAdUrl(null);

    try {
      // Generate AR viewer URL using centralized utility (matching old AR Studio)
      const useMockForAR = import.meta.env.VITE_USE_MOCK_3D === 'true';
      const viewerUrl = generateCompleteARUrl(
        modelId || 'mock',
        thumbnailUrl,
        useMockForAR
      );

      // Debug information (matching old AR Studio)
      const debugInfo = getARUrlDebugInfo();
      console.log('🎯 AR Debug Info:', debugInfo);
      console.log('🎯 Generated AR Viewer URL:', viewerUrl);
      console.log('🎯 Environment Variables:', {
        VITE_API_URL: import.meta.env.VITE_API_URL,
        VITE_AR_HOST_IP: import.meta.env.VITE_AR_HOST_IP,
        VITE_USE_MOCK_3D: import.meta.env.VITE_USE_MOCK_3D
      });
      setArViewerUrl(viewerUrl);

      console.log('🎯 Generated AR Viewer URL:', viewerUrl);
      console.log('🎯 Using original input image ID:', inputImageId);
      console.log('🎯 Using generated 3D model ID:', modelId);

      // Step 1: Generate enhanced prompt using the original input image
      const promptGenerationEndpoint = `${getApiUrl()}/api/v1/ai/generate-ar-ad-prompt`;
      const basePrompt = "Create a realistic AR visual showing a hand holding a smartphone displaying the product in its natural environment. Analyze the product type and place it contextually - kitchen items on counters, bags on tables/dressers, decor on appropriate surfaces, electronics on desks/entertainment centers. The background shows the environment without the product, while the phone screen shows the same space with the product naturally placed. Professional AR demonstration with realistic lighting and proper scale.";

      const promptGenResponse = await fetch(promptGenerationEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify({
          input_media_file_id: inputImageId, // Use the original input image
          base_prompt: basePrompt,
        }),
      });

      let enhancedPrompt = basePrompt;
      if (promptGenResponse.ok) {
        const promptGenResult = await promptGenResponse.json();
        enhancedPrompt = promptGenResult.enhanced_prompt || basePrompt;
      } else {
        console.warn('🎯 Prompt generation failed, using base prompt');
      }

      // Step 2: Generate the AR ad creative using the original input image
      const endpoint = `${getApiUrl()}/api/v1/ai/enhance-product`;
      const payload = {
        input_media_file_id: inputImageId, // Use the original input image, not the 3D model
        description: enhancedPrompt,
        ad_intent: 'product-showcase',
        format_composition: 'with-person-model',
        style_art_direction: 'tech-futuristic',
        skip_prompt_generation: true,
        ar_viewer_url: viewerUrl
      };

      console.log('🎯 AR Ad payload:', payload);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🎯 AR Ad generation failed:', errorText);
        throw new Error(errorText || 'Failed to create AR ad task');
      }

      const result = await response.json();
      console.log('🎯 AR Ad task created:', result);

      if (result.task && result.task.id) {
        startArAdPolling(result.task.id);
      }

      showToast({
        title: "AR Ad Generation Started",
        description: "Your AR content is being generated...",
      });

    } catch (error: any) {
      console.error('🎯 Error creating AR ad:', error);
      showToast({
        title: "Error",
        description: `Failed to start AR ad generation: ${error.message}`,
        variant: "destructive",
      });
      setIsGeneratingArAd(false);
    }
  };

  const generateARAdWithModel = async (modelId: string, thumbnailUrl: string) => {
    setIsGeneratingArAd(true);
    setGeneratedArAdUrl(null);

    try {
      // Generate AR viewer URL
      const viewerUrl = generateCompleteARUrl(modelId, thumbnailUrl, false);
      setArViewerUrl(viewerUrl);

      console.log('🎯 Generated AR Viewer URL:', viewerUrl);

      // For existing 3D models, we need to find an appropriate input image
      // Let's try to find the original image that was used to create this 3D model
      // or use a generic approach

      let inputMediaId = modelId;
      let enhancedPrompt = "Create a realistic AR visual showing a hand holding a smartphone displaying the product in its natural environment. The phone screen shows the product in augmented reality overlaid on a real environment. Professional AR demonstration with realistic lighting and proper scale.";

      // Try to generate enhanced prompt, but don't fail if it doesn't work
      try {
        const promptGenerationEndpoint = `${getApiUrl()}/api/v1/ai/generate-ar-ad-prompt`;
        const basePrompt = "Create a realistic AR visual showing a hand holding a smartphone displaying the product in its natural environment. Analyze the product type and place it contextually - kitchen items on counters, bags on tables/dressers, decor on appropriate surfaces, electronics on desks/entertainment centers. The background shows the environment without the product, while the phone screen shows the same space with the product naturally placed. Professional AR demonstration with realistic lighting and proper scale.";

        const promptGenResponse = await fetch(promptGenerationEndpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
            'X-Client-ID': currentClient.id,
          },
          body: JSON.stringify({
            input_media_file_id: inputMediaId,
            base_prompt: basePrompt,
          }),
        });

        if (promptGenResponse.ok) {
          const promptGenResult = await promptGenResponse.json();
          enhancedPrompt = promptGenResult.enhanced_prompt || enhancedPrompt;
        } else {
          console.warn('Prompt generation failed, using fallback prompt');
        }
      } catch (promptError) {
        console.warn('Prompt generation error, using fallback prompt:', promptError);
      }

      // Step 2: Generate the AR ad creative
      const endpoint = `${getApiUrl()}/api/v1/ai/enhance-product`;
      const payload = {
        input_media_file_id: inputMediaId,
        description: enhancedPrompt,
        ad_intent: 'product-showcase',
        format_composition: 'with-person-model',
        style_art_direction: 'tech-futuristic',
        skip_prompt_generation: true,
        ar_viewer_url: viewerUrl
      };

      console.log('🎯 AR Ad payload:', payload);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🎯 AR Ad generation failed:', errorText);
        throw new Error(errorText || 'Failed to create AR ad task');
      }

      const result = await response.json();
      console.log('🎯 AR Ad task created:', result);

      if (result.task && result.task.id) {
        startArAdPolling(result.task.id);
      }

      showToast({
        title: "AR Ad Generation Started",
        description: "Your AR content is being generated...",
      });

    } catch (error: any) {
      console.error('🎯 Error creating AR ad:', error);
      showToast({
        title: "Error",
        description: `Failed to start AR ad generation: ${error.message}`,
        variant: "destructive",
      });
      setIsGeneratingArAd(false);
    }
  };

  const handleGenerateARAd = async () => {
    if (!formData.input_media_file_id || !generatedMediaId || !currentClient) {
      showToast({
        title: "Error",
        description: "Missing required data for AR ad generation",
        variant: "destructive",
      });
      return;
    }

    // Use the original input image for AR ad generation (not the 3D model)
    const thumbnailUrl = selectedImageUrl || '';
    await generateARAdWithOriginalImage(formData.input_media_file_id, generatedMediaId, thumbnailUrl);
  };

  const startArAdPolling = (taskId: string) => {
    // Clear any existing AR ad polling
    if (arAdPollingInterval) {
      clearInterval(arAdPollingInterval);
    }

    console.log('🎨 Starting AR ad polling for task:', taskId);

    const interval = setInterval(async () => {
      try {
        console.log('🎨 Polling AR ad task:', taskId);
        const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'X-Client-ID': currentClient?.id || '',
          },
        });

        console.log('🎨 AR ad polling response status:', response.status);

        if (response.ok) {
          const task = await response.json();
          console.log('🎨 AR ad task data:', task);
          console.log('🎨 AR ad task status:', task.status);

          if (task.status.toUpperCase() === 'COMPLETED') {
            clearInterval(interval);
            setArAdPollingInterval(null);
            setIsGeneratingArAd(false);

            if (task.output_media_file_ids && task.output_media_file_ids.length > 0 && currentClient) {
              try {
                console.log('🎨 Fetching AR ad media file:', task.output_media_file_ids[0]);
                const mediaResponse = await fetch(`${getApiUrl()}/api/v1/media/${task.output_media_file_ids[0]}`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'X-Client-ID': currentClient.id,
                  },
                });

                if (mediaResponse.ok) {
                  const mediaFile = await mediaResponse.json();
                  console.log('🎨 AR ad media file response:', mediaFile);

                  // Use the proper URL hierarchy: s3_url > cdn_url > file_url > thumbnail_url > API endpoint
                  const arAdUrl = mediaFile.s3_url ||
                    mediaFile.cdn_url ||
                    mediaFile.file_url ||
                    mediaFile.thumbnail_url ||
                    `${getApiUrl()}/api/v1/media/${task.output_media_file_ids[0]}`;

                  console.log('🎨 Setting AR ad URL:', arAdUrl);
                  setGeneratedArAdUrl(arAdUrl);
                } else {
                  console.error('❌ Failed to fetch AR ad media file:', mediaResponse.status);
                }
              } catch (error) {
                console.error('❌ Error fetching AR ad media file:', error);
              }
            }

            showToast({
              title: "AR Ad Generated",
              description: "Your AR content is ready!",
            });
          } else if (task.status.toUpperCase() === 'FAILED') {
            console.log('🎨 AR ad task FAILED:', task.error_message);
            clearInterval(interval);
            setArAdPollingInterval(null);
            setIsGeneratingArAd(false);

            showToast({
              title: "AR Ad Generation Failed",
              description: task.error_message || "An error occurred during AR ad generation.",
              variant: "destructive",
            });
          } else {
            console.log('🎨 AR ad task still in progress, status:', task.status);
          }
        } else {
          console.error('🎨 AR ad polling failed with status:', response.status);
          const errorText = await response.text();
          console.error('🎨 AR ad polling error:', errorText);
        }
      } catch (error) {
        console.error('🎨 Error polling AR ad task status:', error);
      }
    }, 3000);

    setArAdPollingInterval(interval);
  };

  const handleDownloadArAd = async () => {
    if (!generatedArAdUrl) return;

    try {
      const response = await fetch(generatedArAdUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ar-ad-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({
        title: "Download Started",
        description: "Your AR ad image is being downloaded.",
      });
    } catch (error) {
      showToast({
        title: "Download Failed",
        description: "Failed to download the AR ad image.",
        variant: "destructive",
      });
    }
  };



  // Progress tracking with smooth animation
  const [smoothProgress, setSmoothProgress] = useState(0);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);

  // Start timer when generation begins
  useEffect(() => {
    if (isGenerating && !generationStartTime) {
      setGenerationStartTime(Date.now());
      setSmoothProgress(0);
    } else if (!isGenerating) {
      setGenerationStartTime(null);
      setSmoothProgress(0);
    }
  }, [isGenerating]);

  // Smooth progress animation
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isGenerating && generationStartTime) {
      interval = setInterval(() => {
        const elapsed = (Date.now() - generationStartTime) / 1000; // seconds

        // Calculate smooth progress curve (slower at end)
        let calculatedProgress;
        if (elapsed < 30) {
          // Quick start: 0-20% in first 30 seconds
          calculatedProgress = (elapsed / 30) * 20;
        } else if (elapsed < 120) {
          // Steady progress: 20-60% in next 90 seconds
          calculatedProgress = 20 + ((elapsed - 30) / 90) * 40;
        } else if (elapsed < 240) {
          // Slower progress: 60-85% in next 2 minutes
          calculatedProgress = 60 + ((elapsed - 120) / 120) * 25;
        } else {
          // Final stretch: 85-95% in remaining time
          calculatedProgress = Math.min(85 + ((elapsed - 240) / 60) * 10, 95);
        }

        setSmoothProgress(Math.min(calculatedProgress, 95));
      }, 500); // Update every 500ms for smooth animation
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isGenerating, generationStartTime]);

  // Complete progress when task is done
  useEffect(() => {
    if (taskStatus?.is_completed) {
      setSmoothProgress(100);
    }
  }, [taskStatus?.is_completed]);

  const getProgressPercentage = () => {
    if (!isGenerating) return 0;
    if (taskStatus?.is_completed) return 100;
    if (taskStatus?.is_failed) return 0;
    return smoothProgress;
  };

  const getEstimatedTimeRemaining = () => {
    if (!generationStartTime || !isGenerating) return "5 minutes";

    const elapsed = (Date.now() - generationStartTime) / 1000;
    const estimatedTotal = 5 * 60; // 5 minutes
    const remaining = Math.max(0, estimatedTotal - elapsed);

    if (remaining > 60) {
      return `${Math.ceil(remaining / 60)} minutes`;
    } else {
      return `${Math.ceil(remaining)} seconds`;
    }
  };

  const getStatusMessage = () => {
    console.log('🔍 Status check - isGenerating:', isGenerating, 'modelUrl:', modelUrl, 'taskStatus:', taskStatus);
    if (!isGenerating && !modelUrl) return "Ready to generate 3D model";
    if (isGenerating && !taskStatus) return "Starting 3D generation...";
    if (taskStatus) {
      if (taskStatus.is_completed) return "3D model generated successfully!";
      if (taskStatus.is_failed) return `Generation failed: ${taskStatus.error_message}`;
      return "Generating 3D model...";
    }
    if (modelUrl) return "3D model ready for viewing";
    return "Processing...";
  };

  return (
    <TooltipProvider>
      {/* Static Info Banner */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <Box className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                3D Model Generation
              </h3>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                If you navigate away while task is running, you can track your task progress in the Tasks page.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/app/content-creation/tasks')}
            className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-800/30"
          >
            View Tasks
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-8">
        {/* Left Panel - Form */}
        <div className="xl:col-span-4 space-y-6">
          <Card data-tour="image-upload-section">
            <CardContent className="pt-6">
              <ImageTo3DForm
                onSubmit={handleStartGeneration}
                isLoading={isGenerating}
                formData={formData}
                setFormData={setFormData}
              />

              <div className="mt-6 pt-4 border-t border-border/50" data-tour="generate-3d-button">
                    <Button
                      onClick={() => handleStartGeneration(formData)}
                      disabled={!formData.input_media_file_id || isGenerating || imageTo3DTask.isLimitReached}
                      className={`w-full h-12 font-semibold text-sm rounded-lg shadow-lg transition-all duration-300 ${imageTo3DTask.isLimitReached
                        ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60'
                        : 'text-white transform hover:scale-105 disabled:opacity-50 disabled:transform-none'
                        }`}
                      style={!imageTo3DTask.isLimitReached ? { 
                        background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                        backgroundSize: '200% 200%',
                        backgroundPosition: 'left center',
                        boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
                      } : {}}
                      onMouseEnter={(e) => {
                        if (!imageTo3DTask.isLimitReached) {
                          e.currentTarget.style.backgroundPosition = 'right center';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!imageTo3DTask.isLimitReached) {
                          e.currentTarget.style.backgroundPosition = 'left center';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3)';
                        }
                      }}
                    >
                      {isGenerating ? (
                        <div className="flex items-center space-x-2">
                          <div className="relative">
                            <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <img
                                src="/icon-light.png"
                                alt="Quixocial"
                                className="w-2 h-2"
                              />
                            </div>
                          </div>
                          <span>Generating...</span>
                        </div>
                      ) : imageTo3DTask.isLimitReached ? (
                        <span>Limit Reached</span>
                      ) : (
                        <div className="flex items-center justify-between w-full px-2">
                          <div className="flex items-center space-x-2">
                            <Box className="w-4 h-4" />
                            <span>Generate 3D Model</span>
                          </div>
                          <div className="flex items-center space-x-1 bg-white/20 px-2 py-1 rounded-full">
                            <span className="text-xs font-bold">{imageTo3DTask.cost}</span>
                            <span className="text-xs">credits</span>
                          </div>
                        </div>
                      )}
                    </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Canvas */}
        <div className="xl:col-span-8">
          <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border border-gray-200 shadow-xl shadow-slate-500/5 min-h-[450px] lg:min-h-[650px]">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-r from-card/95 to-card/80 backdrop-blur-xl border-b border-border/30 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)', boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)' }}>
                    <Box className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-card-foreground">3D Model Canvas</h3>
                </div>
                <div className="flex items-center space-x-2">
                  {isGenerating && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      className="text-muted-foreground text-xs sm:text-sm"
                    >
                      <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      Stop
                    </Button>
                  )}
                  {modelUrl && (
                    <div data-tour="action-buttons">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDownload3DModel}
                        className="text-muted-foreground text-xs sm:text-sm"
                      >
                        <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleCreateARAdFromModel}
                        className="text-white text-xs sm:text-sm"
                        style={{ 
                          background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                          backgroundSize: '200% 200%',
                          backgroundPosition: 'left center',
                          boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundPosition = 'right center';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundPosition = 'left center';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3)';
                        }}
                      >
                        <Smartphone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Create AR Ad
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="absolute inset-0 pt-16 sm:pt-20 p-4 sm:p-6">
              <div className="w-full h-full flex items-center justify-center">
                {isGenerating ? (
                  <div className="text-center space-y-6">
                    {/* Progress Circle */}
                    <div className="relative w-32 h-32 mx-auto">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          className="text-slate-200 dark:text-slate-700"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          stroke="url(#progressGradient)"
                          strokeWidth="4"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 42}`}
                          strokeDashoffset={`${2 * Math.PI * 42 * (1 - getProgressPercentage() / 100)}`}
                          className="transition-all duration-700 ease-out"
                          strokeLinecap="round"
                        />
                        <defs>
                          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgb(59, 130, 246)" />
                            <stop offset="50%" stopColor="rgb(139, 92, 246)" />
                            <stop offset="100%" stopColor="rgb(236, 72, 153)" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Box className="w-8 h-8 text-primary animate-pulse" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-xl font-bold">Generating 3D Model</h3>
                      <p className="text-muted-foreground">
                        Converting your image to a 3D model • Estimated time remaining: {getEstimatedTimeRemaining()}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Progress: {Math.round(getProgressPercentage())}%
                      </p>
                    </div>
                  </div>
                ) : modelUrl ? (
                  <div className="w-full h-full space-y-4">
                    {/* 3D Model Viewer */}
                    <div className="w-full h-96 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl border border-border/50 overflow-hidden" data-tour="3d-preview-canvas">
                      <model-viewer
                        src={modelUrl}
                        alt="Generated 3D Model"
                        camera-controls
                        auto-rotate
                        style={{ width: '100%', height: '100%' }}
                        className="w-full h-full"
                        onLoad={handleModelLoad}
                        onError={handleModelError}
                      />
                      {isModelLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                          <div className="relative">
                            {/* Spinning ring */}
                            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                            {/* Logo in center */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <img
                                src="/icon-light.png"
                                alt="Quixocial"
                                className="w-8 h-8 animate-pulse"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Success Message */}
                    <div className="text-center space-y-3">
                      <h3 className="text-xl font-bold text-green-600">🎉 3D Model Ready!</h3>
                      <p className="text-muted-foreground">Your 3D model has been generated successfully</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-6">
                    <div className="w-32 h-32 bg-gradient-to-br from-muted to-accent rounded-2xl flex items-center justify-center shadow-inner mx-auto">
                      <Box className="w-16 h-16 text-muted-foreground" />
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-2xl font-bold">3D Model Preview</h3>
                      <p className="text-muted-foreground max-w-lg">
                        Upload an image and generate a high-quality 3D model using AI. Perfect for AR showcase of products.
                      </p>
                      <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground pt-2">
                        <div className="flex items-center space-x-2">
                          <Sparkles className="w-4 h-4 text-blue-500" />
                          <span>AI-Powered</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Box className="w-4 h-4 text-purple-500" />
                          <span>GLB Format</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Download className="w-4 h-4 text-green-500" />
                          <span>Ready to Use</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};